import { useState } from "react";
import { Key, SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ChangeCredentialsModal({ open, onClose, testid = "change-credentials" }) {
    const { user, updateUser } = useAuth();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newUsername, setNewUsername] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const reset = () => {
        setCurrentPassword("");
        setNewUsername("");
        setNewPassword("");
        setConfirmPassword("");
        setError("");
        setSaving(false);
    };

    const handleOpenChange = (value) => {
        if (!value) {
            reset();
            onClose();
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        const username = newUsername.trim();
        if (!username && !newPassword) {
            setError("Isi username baru atau password baru.");
            return;
        }
        if (username && /\s/.test(username)) {
            setError("Username tidak boleh mengandung spasi.");
            return;
        }
        if (username && username.length < 3) {
            setError("Username minimal 3 karakter.");
            return;
        }
        if (newPassword && newPassword.length < 6) {
            setError("Password baru minimal 6 karakter.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setError("Konfirmasi password tidak sama.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/auth/change-credentials", {
                current_password: currentPassword,
                new_username: username,
                new_password: newPassword,
            });
            updateUser(data);
            toast.success("Username/password berhasil diperbarui");
            reset();
            onClose();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const isGoogleAccount = user?.auth_provider === "google";

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-md" data-testid={`${testid}-modal`}>
                <DialogHeader>
                    <DialogTitle className="font-display uppercase text-xl flex items-center gap-2">
                        <Key size={20} className="text-racing-red" /> Ubah Username & Password
                    </DialogTitle>
                    <DialogDescription className="text-sm text-racing-muted">
                        Akun: <span className="font-medium text-neutral-900">{user?.username}</span> ({user?.email})
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-4 mt-2" data-testid={`${testid}-form`}>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Password Saat Ini</label>
                        <input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            data-testid={`${testid}-current-password`}
                            className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red"
                            placeholder={isGoogleAccount ? "Kosongkan jika akun dibuat via Google" : "Wajib diisi"}
                        />
                        {isGoogleAccount && (
                            <p className="mt-1.5 text-xs text-racing-muted">Akun Anda tertaut Google. Jika belum pernah membuat password, kolom ini boleh dikosongkan.</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Username Baru (opsional)</label>
                        <input
                            type="text"
                            value={newUsername}
                            onChange={(e) => setNewUsername(e.target.value)}
                            data-testid={`${testid}-new-username`}
                            className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red"
                            placeholder={user?.username}
                        />
                        <p className="mt-1.5 text-xs text-racing-muted">Tanpa spasi, minimal 3 karakter. Biarkan kosong jika tidak ingin mengganti.</p>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Password Baru (opsional)</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            data-testid={`${testid}-new-password`}
                            className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red"
                            placeholder="Minimal 6 karakter"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Ulangi Password Baru</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            data-testid={`${testid}-confirm-password`}
                            className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red"
                            placeholder="Ketik ulang password baru"
                        />
                    </div>

                    {error && <p className="text-sm text-racing-red" data-testid={`${testid}-error`}>{error}</p>}

                    <button
                        type="submit"
                        disabled={saving}
                        data-testid={`${testid}-submit`}
                        className="w-full bg-racing-red hover:bg-racing-redhover disabled:opacity-60 text-white text-xs uppercase tracking-[0.25em] py-3.5 transition-colors duration-300 flex items-center justify-center gap-2"
                    >
                        {saving && <SpinnerGap size={16} className="animate-spin" />} Simpan Perubahan
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
